import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import { addressesIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  PickTicketsItemCreateMutationSchema,
  PickTicketsItemMutationSchema,
  PickTicketsItemQuerySchema,
} from './pick-ticket-items-schemas';
import type { PickTicketsCreateSchema, PickTicketsMutationSchema } from './schemas';
import type { PickTicketsQueryAllSchema, PickTicketsQuerySchema } from './schemas/pick-tickets-query-schema';

export function getPickTicketsAllQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['PICK_TICKETS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<PickTicketsQueryAllSchema>({
        endpoint: '/api/v1/pick-tickets',
        params: `${apiParamStr}&include=client.logo,picker.profile_images,contacts.profile_image`,
      });

      return res;
    },
  };
}

export function useGetAllPickTickets(apiParamStr: string) {
  return useSuspenseQuery(getPickTicketsAllQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PICK_TICKETS'] });
};

export function useGetPickTickets(id: string) {
  return useSuspenseQuery(getPickTicketsQuery(id));
}

export function getPickTicketsQuery(id: string) {
  return {
    queryKey: ['PICK_TICKET', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: PickTicketsQuerySchema }>({
        endpoint: `/api/v1/pick-tickets/${id}`,
        params: `include=pick_ticket_items.inventory.inventory_category,picker.profile_images,attached_documents,client.logo,client.contacts,contacts.profile_image,contacts,${addressesIncludeKey('delivery_address', true)}`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidatePickTicketsQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['PICK_TICKET', id] });
};

export const useGetPickTicketsMutation: UseControlledMutation<PickTicketsQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['PICK_TICKETS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<PickTicketsQueryAllSchema>({
            endpoint: '/api/v1/pick-tickets',
            params: { search, per_page },
          });

          return res?.data || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: PickTicketsQuerySchema }, unknown, PickTicketsCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/pick-tickets/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: PickTicketsQuerySchema }, ApiError, PickTicketsMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/pick-tickets/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'pick-tickets', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: PickTicketsQuerySchema }, ApiError, Partial<PickTicketsMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/pick-tickets/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['PICK_TICKET_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/pick-tickets/${id}`,
        method: 'DELETE',
      }),
  });

export const usePickTicketsItemsCreateMutation = () =>
  useMutation<{ data: PickTicketsItemQuerySchema }, ApiError, PickTicketsItemCreateMutationSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/pick-ticket-items`,
        method: 'POST',
        payload,
      }),
  });

export const usePickTicketsItemsBulkUpdateMutation = () =>
  useMutation<{ data: PickTicketsItemQuerySchema }, ApiError, { items: PickTicketsItemMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/pick-ticket-items/bulk-update`,
        method: 'POST',
        payload,
      }),
  });

export const usePickTicketsItemsBulkDeleteMutation = () =>
  useMutation<{ data: PickTicketsItemQuerySchema }, ApiError, { items: number[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/pick-ticket-items/bulk-delete`,
        method: 'POST',
        payload,
      }),
  });

export const useGetPickTicketsPDFMutation = (id: string) =>
  useMutation<
    {
      data: {
        name: string;
        pdf_url: string;
      };
    },
    ApiError
  >({
    mutationFn: () =>
      apiClient.request({
        endpoint: `/api/v1/pick-ticket-receipts/${id}`,
        method: 'GET',
        init: { headers: { 'Content-Type': 'application/pdf', 'response-type': 'blob' } },
      }),
  });
